import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { createProposal, getProposals, updateProposalStatus, getProposalsSummary, getProposalDetail, attachProposalFile, emailProposalToRep, uploadFile, deleteProposal, getAudit, API_BASE } from "../lib/api";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "../components/ui/dialog";
import {
  Loader2, Plus, FileSearch, DollarSign, GripVertical, TrendingUp, XCircle, Paperclip, Mail, Clock, Calendar, Building2, Trash2, Sparkles, FileText, Download, Presentation, AlertCircle, Wand2, Eye,
} from "lucide-react";
import { Link } from "react-router-dom";
import { AuditBuilder, AuditHistoryList, AuditSendsList, EmailAuditDialog } from "../components/AuditBuilder";

const AUDIT_OPTIONS = [
  { id: "prospect_id_lookup", label: "ProspectID data lookup for buyer intent and site traffic estimate" },
  { id: "seo_site_audit", label: "Website SEO/AEO Site Audit with recommended corrections/cost for 1x tune-up" },];

const PROPOSAL_OPTIONS = [
  { id: "marketing_proposal", label: "Marketing Proposal (Includes ProspectID data and site traffic)" },
  { id: "website_design_proposal", label: "Website Design Proposal" },
  { id: "seo_aeo_proposal", label: "SEO/AEO Proposal" },
];

const KANBAN_COLS = [
  { id: "requested", label: "Proposal Requested", color: "bg-amber-50", headerColor: "text-amber-700", icon: FileSearch },
  { id: "created", label: "Proposal Created", color: "bg-blue-50", headerColor: "text-blue-700", icon: FileSearch },
  { id: "presented", label: "Proposal Presented", color: "bg-indigo-50", headerColor: "text-indigo-700", icon: FileSearch },
  { id: "sold", label: "Proposal Sold", color: "bg-green-50", headerColor: "text-green-700", icon: TrendingUp },
  { id: "lost", label: "Proposal Lost", color: "bg-zinc-100", headerColor: "text-zinc-500", icon: XCircle },
];

export default function ProposalsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [proposals, setProposals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Detail view state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailProposal, setDetailProposal] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [emailingRep, setEmailingRep] = useState(false);
  const [deletingProposal, setDeletingProposal] = useState(false);

  const handleDeleteProposal = async () => {
    if (!detailProposal) return;
    const confirmMsg = `Permanently delete the proposal for ${detailProposal.client_company || detailProposal.client_name || "this client"}? This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    setDeletingProposal(true);
    try {
      await deleteProposal(detailProposal.id);
      toast.success("Proposal deleted");
      setDetailOpen(false);
      setDetailProposal(null);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete proposal");
    } finally {
      setDeletingProposal(false);
    }
  };
  const [emailMessage, setEmailMessage] = useState("");
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    business_name: "", industry: "", website_url: "",
    sales_rep: user?.name || "", sales_rep_email: user?.email || "",
    presentation_date: "",
    audit_types: [], proposal_types: [],
    form_data: { geo_target: "", ideal_prospect: "", tactics: "", comment: "", customer_goals: "", page_count: "", new_functionality: "", example_site_1: "", example_site_2: "", example_site_3: "", seo_scope: "", trading_area: "", campaign_length: "" },
    budget_low: "", budget_high: "",
    self_fulfilled: false,
  });

  const fetchData = async () => {
    try {
      const [p, s] = await Promise.all([getProposals(), getProposalsSummary()]);
      setProposals(p.data);
      setSummary(s.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  // Auto-open proposal detail when ?open=<id> is in URL
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && !detailOpen) {
      openDetail(openId);
      // Clean URL after opening
      const np = new URLSearchParams(searchParams);
      np.delete("open");
      setSearchParams(np, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const toggleList = (key, val) => {
    setForm((p) => ({ ...p, [key]: p[key].includes(val) ? p[key].filter((v) => v !== val) : [...p[key], val] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.business_name) { toast.error("Business name required"); return; }
    setSubmitting(true);
    try {
      await createProposal(form);
      toast.success("Proposal submitted!");
      setDialogOpen(false);
      setForm({ ...form, business_name: "", industry: "", website_url: "", presentation_date: "", audit_types: [], proposal_types: [], form_data: { geo_target: "", ideal_prospect: "", tactics: "", comment: "", customer_goals: "", page_count: "", new_functionality: "", example_site_1: "", example_site_2: "", example_site_3: "", seo_scope: "", trading_area: "", campaign_length: "" }, budget_low: "", budget_high: "", self_fulfilled: false });
      fetchData();
    } catch (e) { toast.error("Failed to submit"); }
    finally { setSubmitting(false); }
  };

  const openDetail = async (proposalId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setEmailMessage("");
    try {
      const res = await getProposalDetail(proposalId);
      setDetailProposal(res.data);
    } catch (e) {
      toast.error("Failed to load proposal");
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleFileAttach = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !detailProposal) return;
    setUploadingFile(true);
    try {
      const upRes = await uploadFile(file);
      await attachProposalFile(detailProposal.id, upRes.data.id);
      toast.success("File attached");
      const refreshed = await getProposalDetail(detailProposal.id);
      setDetailProposal(refreshed.data);
      fetchData();
    } catch (err) {
      toast.error("Failed to attach file");
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleEmailToRep = async () => {
    if (!detailProposal) return;
    if (!detailProposal.attached_file_id && !emailMessage.trim()) {
      toast.error("Add a message (e.g., Google Doc link) or attach a file before sending");
      return;
    }
    setEmailingRep(true);
    try {
      await emailProposalToRep(detailProposal.id, emailMessage);
      toast.success(`Emailed to ${detailProposal.sales_rep_email}`);
      const refreshed = await getProposalDetail(detailProposal.id);
      setDetailProposal(refreshed.data);
    } catch (err) {
      toast.error("Failed to send email");
    } finally {
      setEmailingRep(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const { draggableId, destination } = result;
    const newStatus = destination.droppableId;
    setProposals((prev) => prev.map((p) => p.id === draggableId ? { ...p, status: newStatus } : p));
    try {
      await updateProposalStatus(draggableId, newStatus);
      fetchData();
    } catch (e) { toast.error("Failed to update"); fetchData(); }
  };

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const columns = KANBAN_COLS.map((col) => ({ ...col, items: proposals.filter((p) => p.status === col.id) }));

  const hasMarketing = form.proposal_types.includes("marketing_proposal");
  const hasWebDesign = form.proposal_types.includes("website_design_proposal");
  const hasSEO = form.proposal_types.includes("seo_aeo_proposal");
  const hasAudit = form.audit_types.includes("seo_site_audit");
  const websiteRequired = hasSEO || hasAudit;
  const websiteMissing = websiteRequired && !form.website_url.trim();

  return (
    <div className="space-y-6" data-testid="proposals-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight">Proposals & Audits</h1>
          <p className="text-muted-foreground mt-1">Request proposals, track pipeline</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild><Button data-testid="new-proposal-btn"><Plus className="mr-2 h-4 w-4" />New Proposal Request</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Proposal & Audit Request</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Business Info */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm border-b pb-1">Business Information</h3>
                <Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Business Name of Client *" required data-testid="prop-business-name" />
                <div className="grid grid-cols-2 gap-3">
                  <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="Client Industry" />
                  <div>
                    <Input
                      value={form.website_url}
                      onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                      placeholder={websiteRequired ? "Client Website URL *" : "Client Website URL"}
                      required={websiteRequired}
                      type={websiteRequired ? "url" : "text"}
                      data-testid="prop-website-url"
                      className={websiteMissing ? "border-amber-400" : ""}
                    />
                    {websiteRequired && (
                      <p className="text-[11px] mt-1 text-amber-700">
                        Required — we&apos;ll auto-run a Claude SEO + AEO audit on this URL when you submit.
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input value={form.sales_rep} onChange={(e) => setForm({ ...form, sales_rep: e.target.value })} placeholder="Sales Rep Name" />
                  <Input value={form.sales_rep_email} onChange={(e) => setForm({ ...form, sales_rep_email: e.target.value })} placeholder="Sales Rep Email" />
                </div>
                <div><Label className="text-xs text-muted-foreground">Presentation Date</Label><Input type="date" value={form.presentation_date} onChange={(e) => setForm({ ...form, presentation_date: e.target.value })} data-testid="prop-date" /></div>
              </div>

              {/* Audits */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm border-b pb-1">Client Audits</h3>
                {AUDIT_OPTIONS.map((opt) => (
                  <label key={opt.id} className="flex items-start gap-2 cursor-pointer">
                    <Checkbox checked={form.audit_types.includes(opt.id)} onCheckedChange={() => toggleList("audit_types", opt.id)} className="mt-0.5" />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                ))}
                {form.audit_types.includes("prospect_id_lookup") && (
                  <div className="ml-6"><Label className="text-xs">Trading Area / Target GEO</Label><Input value={form.form_data.trading_area} onChange={(e) => setForm({ ...form, form_data: { ...form.form_data, trading_area: e.target.value } })} placeholder="Zips, city, county, state etc" /></div>
                )}
              </div>

              {/* Proposals */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm border-b pb-1">Proposals</h3>
                {PROPOSAL_OPTIONS.map((opt) => (
                  <label key={opt.id} className="flex items-start gap-2 cursor-pointer">
                    <Checkbox checked={form.proposal_types.includes(opt.id)} onCheckedChange={() => toggleList("proposal_types", opt.id)} className="mt-0.5" />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>

              {/* Marketing Proposal Fields */}
              {hasMarketing && (
                <div className="space-y-3 p-3 rounded-lg border bg-orange-50/50">
                  <h4 className="font-semibold text-sm">Marketing Proposal Details</h4>
                  <Input value={form.form_data.geo_target} onChange={(e) => setForm({ ...form, form_data: { ...form.form_data, geo_target: e.target.value } })} placeholder="Geo Target" />
                  <Input value={form.form_data.ideal_prospect} onChange={(e) => setForm({ ...form, form_data: { ...form.form_data, ideal_prospect: e.target.value } })} placeholder="Ideal Target Prospect (Homeowner, Women, Age, Income, Interests etc)" />
                  <Input value={form.form_data.tactics} onChange={(e) => setForm({ ...form, form_data: { ...form.form_data, tactics: e.target.value } })} placeholder="Tactics You'd Like to See in the Proposal (ProspectID, Email, Google SEM, Meta, Display etc)" />
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Monthly Budget Low</Label><Input value={form.budget_low} onChange={(e) => setForm({ ...form, budget_low: e.target.value })} placeholder="$" /></div>
                    <div><Label className="text-xs">Monthly Budget High</Label><Input value={form.budget_high} onChange={(e) => setForm({ ...form, budget_high: e.target.value })} placeholder="$" /></div>
                  </div>
                  <div><Label className="text-xs">Estimated Campaign Length (# of Months)</Label><Input type="number" min="1" value={form.form_data.campaign_length} onChange={(e) => setForm({ ...form, form_data: { ...form.form_data, campaign_length: e.target.value } })} placeholder="e.g. 6" data-testid="prop-campaign-length" /></div>
                  <Textarea value={form.form_data.comment} onChange={(e) => setForm({ ...form, form_data: { ...form.form_data, comment: e.target.value } })} placeholder="What else should we know?" rows={2} />
                </div>
              )}

              {/* Website Design Proposal Fields */}
              {hasWebDesign && (
                <div className="space-y-3 p-3 rounded-lg border bg-blue-50/50">
                  <h4 className="font-semibold text-sm">Website Design Proposal Details</h4>
                  <Textarea value={form.form_data.customer_goals} onChange={(e) => setForm({ ...form, form_data: { ...form.form_data, customer_goals: e.target.value } })} placeholder="Customer Goals / Why do they want a new site? (lead gen, updated look, stability)" rows={2} />
                  <Input value={form.form_data.page_count} onChange={(e) => setForm({ ...form, form_data: { ...form.form_data, page_count: e.target.value } })} placeholder="Estimated Number of Pages" />
                  <Input value={form.form_data.new_functionality} onChange={(e) => setForm({ ...form, form_data: { ...form.form_data, new_functionality: e.target.value } })} placeholder="New Functionality Required? (ecommerce, calendar, member area)" />
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Budget Low</Label><Input value={form.budget_low} onChange={(e) => setForm({ ...form, budget_low: e.target.value })} placeholder="$" /></div>
                    <div><Label className="text-xs">Budget High</Label><Input value={form.budget_high} onChange={(e) => setForm({ ...form, budget_high: e.target.value })} placeholder="$" /></div>
                  </div>
                  <h5 className="text-xs font-medium text-muted-foreground mt-2">Example Sites</h5>
                  <Input value={form.form_data.example_site_1} onChange={(e) => setForm({ ...form, form_data: { ...form.form_data, example_site_1: e.target.value } })} placeholder="Site 1 URL" />
                  <Input value={form.form_data.example_site_2} onChange={(e) => setForm({ ...form, form_data: { ...form.form_data, example_site_2: e.target.value } })} placeholder="Site 2 URL" />
                  <Input value={form.form_data.example_site_3} onChange={(e) => setForm({ ...form, form_data: { ...form.form_data, example_site_3: e.target.value } })} placeholder="Site 3 URL" />
                </div>
              )}

              {/* SEO/AEO Proposal Fields */}
              {hasSEO && (
                <div className="space-y-3 p-3 rounded-lg border bg-green-50/50">
                  <h4 className="font-semibold text-sm">SEO/AEO Proposal Scope</h4>
                  {["Complete SEO/AEO Audit", "1x Tune-Up Only", "Tune-Up + Ongoing SEO/AEO Content Management"].map((opt) => (
                    <label key={opt} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox checked={form.form_data.seo_scope === opt} onCheckedChange={() => setForm({ ...form, form_data: { ...form.form_data, seo_scope: opt } })} />
                      <span className="text-sm">{opt}</span>
                    </label>
                  ))}
                </div>
              )}

              {/* Self-fulfilled flag — skip the "Awaiting RP Team" queue */}
              <label
                className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border bg-violet-50/50 border-violet-200"
                data-testid="self-fulfilled-block"
              >
                <Checkbox
                  checked={!!form.self_fulfilled}
                  onCheckedChange={(v) => setForm({ ...form, self_fulfilled: !!v })}
                  className="mt-0.5"
                  data-testid="self-fulfilled-checkbox"
                />
                <span className="text-sm">
                  <span className="font-semibold text-violet-900">I&apos;m fulfilling this myself (self-serve)</span>
                  <br />
                  <span className="text-xs text-violet-800/80">
                    Skip the &ldquo;Awaiting RP Team&rdquo; queue — drops straight into &ldquo;Proposal Created&rdquo;.
                    Use this when you&apos;re running the audit and downloading the branded deck on your own.
                    {hasSEO && " The auto-audit still runs so you can grab the branded deck."}
                  </span>
                </span>
              </label>

              <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-proposal">
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting...</> : "Submit Proposal Request"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Stats — Pipeline snapshot at top for quick scan */}
      {summary && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="border bg-amber-50"><CardContent className="pt-6">
            <div className="flex items-center gap-3"><FileSearch className="h-8 w-8 text-amber-600" /><div><p className="text-2xl font-bold">{summary.pending_count}</p><p className="text-sm text-muted-foreground">Pending &middot; ${summary.pending_value?.toLocaleString()}</p></div></div>
          </CardContent></Card>
          <Card className="border bg-green-50"><CardContent className="pt-6">
            <div className="flex items-center gap-3"><TrendingUp className="h-8 w-8 text-green-600" /><div><p className="text-2xl font-bold">{summary.sold_count}</p><p className="text-sm text-muted-foreground">Sold &middot; ${summary.sold_value?.toLocaleString()}</p></div></div>
          </CardContent></Card>
          <Card className="border"><CardContent className="pt-6">
            <div className="flex items-center gap-3"><XCircle className="h-8 w-8 text-zinc-400" /><div><p className="text-2xl font-bold">{summary.lost_count}</p><p className="text-sm text-muted-foreground">Lost</p></div></div>
          </CardContent></Card>
        </div>
      )}

      {/* Kanban Board — Pending Proposals at the top so admins can act on
          requests that need our help before browsing the self-serve tools below. */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 min-h-[400px]">
          {columns.map((col) => (
            <Droppable droppableId={col.id} key={col.id}>
              {(provided, snapshot) => (
                <div ref={provided.innerRef} {...provided.droppableProps}
                  data-testid={`kanban-col-${col.id}`}
                  className={`rounded-lg p-3 ${col.color} ${snapshot.isDraggingOver ? "ring-2 ring-primary/30" : ""}`}>
                  <div className="flex items-center justify-between mb-3 px-1">
                    <h3 className={`font-semibold text-sm ${col.headerColor}`}>{col.label}</h3>
                    <Badge variant="outline" className="text-xs" data-testid={`kanban-count-${col.id}`}>{col.items.length}</Badge>
                  </div>
                  {/* No internal ScrollArea — let the page scroll so every
                      proposal is reachable and nothing gets clipped off-screen. */}
                  <div className="space-y-2 min-h-[80px]">
                    {col.items.map((proposal, index) => {
                      // Self-built (Prospect IQ) rows have `source==='prospect_iq'`
                      // and can't be re-columned via drag (they don't live in the
                      // proposals collection). Route interactions accordingly.
                      const isSelfBuilt = proposal.source === "prospect_iq";
                      // "View Proposal" download URL — legacy uses the attached
                      // file, self-built uses the generated deck endpoint.
                      const downloadUrl = isSelfBuilt
                        ? `${API_BASE}/api/prospect-proposals/${proposal.id}/download?format=pptx`
                        : (proposal.attached_file_id
                            ? `${API_BASE}/api/files/${proposal.attached_file_id}/download`
                            : null);
                      return (
                      <Draggable draggableId={proposal.id} index={index} key={proposal.id} isDragDisabled={isSelfBuilt}>
                        {(provided, snapshot) => (
                          <div ref={provided.innerRef} {...provided.draggableProps}
                            onClick={() => { if (!isSelfBuilt) openDetail(proposal.id); }}
                            data-testid={`proposal-card-${proposal.id}`}
                            className={`bg-white rounded-lg border p-3 shadow-sm ${isSelfBuilt ? "cursor-default" : "cursor-pointer"} ${snapshot.isDragging ? "shadow-lg ring-2 ring-primary/20" : (!isSelfBuilt ? "hover:shadow-md hover:ring-1 hover:ring-primary/20" : "hover:shadow-md")}`}>
                            <div className="flex items-start gap-2">
                              <div {...provided.dragHandleProps} onClick={(e) => e.stopPropagation()}
                                   className={`mt-0.5 ${isSelfBuilt ? "invisible" : "cursor-grab"}`}>
                                <GripVertical className="h-4 w-4 text-zinc-300" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-sm">{proposal.business_name}</p>
                                  {isSelfBuilt && (
                                    <Badge variant="outline" className="text-[9px] h-4 bg-indigo-50 text-indigo-700 border-indigo-200">
                                      <Sparkles className="h-2.5 w-2.5 mr-0.5" /> Prospect IQ
                                    </Badge>
                                  )}
                                </div>
                                {proposal.industry && <p className="text-xs text-muted-foreground">{proposal.industry}</p>}
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {proposal.proposal_types?.map((t) => <Badge key={t} variant="outline" className="text-[10px] h-4">{t.replace(/_/g, " ")}</Badge>)}
                                  {proposal.audit_types?.map((t) => <Badge key={t} variant="outline" className="text-[10px] h-4 bg-purple-50">{t.replace(/_/g, " ")}</Badge>)}
                                </div>
                                <div className="flex items-center justify-between mt-2">
                                  <div className="flex items-center gap-1">
                                    <DollarSign className="h-3 w-3 text-green-600" />
                                    <span className="text-xs font-semibold text-green-700">${proposal.estimated_value?.toLocaleString()}</span>
                                  </div>
                                  <span className="text-[10px] text-muted-foreground">{proposal.sales_rep}</span>
                                </div>
                                {/* View Proposal button — shows when we have
                                    something to download (attached file for
                                    legacy, generated deck for self-built). */}
                                {downloadUrl && (
                                  <a
                                    href={downloadUrl}
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 mt-2 px-2 py-1 text-[11px] font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition"
                                    data-testid={`view-proposal-${proposal.id}`}
                                  >
                                    <Eye className="h-3 w-3" /> View Proposal
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>

      {/* ---- Self-serve tools (below the pipeline) ---- */}
      {/* AEO/SEO Instant Audit hero — Phase 2 */}
      <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <CardContent className="pt-6 pb-5">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 p-2 shrink-0">
                <FileSearch className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="font-outfit text-xl font-bold text-zinc-900">AEO / SEO Audit</h2>
                <p className="text-sm text-muted-foreground mt-0.5">Get an instant audit — Claude analyzes your client&apos;s website and grades it A&ndash;F on SEO + AEO with concrete recommendations and quick wins.</p>
              </div>
            </div>
            <AuditBuilder />
          </div>
        </CardContent>
      </Card>

      {/* Homepage Mockup Builder hero — Claude Sonnet 4.5 drafts a live hero-page
          mockup a rep can share with a prospect, drop into a slide, or download
          as a PDF. Sits next to AEO/SEO because both are pre-sale tools. */}
      <Card className="border-2 border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 via-white to-indigo-50">
        <CardContent className="pt-6 pb-5">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-gradient-to-br from-fuchsia-600 to-indigo-600 p-2 shrink-0">
                <Wand2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="font-outfit text-xl font-bold text-zinc-900">Homepage Mockup Builder</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Show a prospect what their new homepage could look like — Claude drafts a self-contained hero page
                  in ~60–90s. Share the live link, drop the PNG into your deck, or send the PDF.
                </p>
              </div>
            </div>
            <Button
              asChild
              className="bg-gradient-to-r from-fuchsia-600 to-indigo-600 hover:from-fuchsia-700 hover:to-indigo-700 shrink-0"
              data-testid="open-mockup-builder-proposals"
            >
              <Link to="/mockups"><Wand2 className="mr-1 h-4 w-4" /> Build a Mockup</Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Past audits */}
      <div>
        <h3 className="font-outfit text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Recent Audits</h3>
        <AuditHistoryList />
      </div>

      {/* Sent audits with open / view tracking */}
      <div>
        <h3 className="font-outfit text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Sent Audits — Open & View Tracking</h3>
        <AuditSendsList />
      </div>

      {/* Proposal Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="proposal-detail-dialog">
          {detailLoading || !detailProposal ? (
            <div className="py-12 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  {detailProposal.business_name}
                </DialogTitle>
                <DialogDescription>
                  {detailProposal.industry || "—"} {detailProposal.website_url ? `• ${detailProposal.website_url}` : ""}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-5 mt-2">
                {/* Status / Due / Value strip */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg border p-3 bg-zinc-50">
                    <p className="text-[10px] uppercase text-muted-foreground tracking-wide">Status</p>
                    <Badge className={`mt-1 ${detailProposal.status === "sold" ? "bg-green-100 text-green-800" : detailProposal.status === "lost" ? "bg-zinc-200 text-zinc-700" : detailProposal.status === "presented" ? "bg-indigo-100 text-indigo-800" : detailProposal.status === "created" ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"}`}>
                      {detailProposal.status}
                    </Badge>
                  </div>
                  <div className="rounded-lg border p-3 bg-zinc-50">
                    <p className="text-[10px] uppercase text-muted-foreground tracking-wide flex items-center gap-1"><Clock className="h-3 w-3" /> Due (48h)</p>
                    <p className="text-sm font-medium mt-1">
                      {detailProposal.due_date ? new Date(detailProposal.due_date).toLocaleString() : "—"}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3 bg-green-50">
                    <p className="text-[10px] uppercase text-green-700 tracking-wide">Estimated Value</p>
                    <p className="text-lg font-bold text-green-800 mt-1">${detailProposal.estimated_value?.toLocaleString()}</p>
                  </div>
                </div>

                {/* Rep info */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><Label className="text-xs text-muted-foreground">Sales Rep</Label><p className="font-medium">{detailProposal.sales_rep || "—"}</p></div>
                  <div><Label className="text-xs text-muted-foreground">Rep Email</Label><p className="font-medium">{detailProposal.sales_rep_email || "—"}</p></div>
                  <div><Label className="text-xs text-muted-foreground">Presentation Date</Label><p className="font-medium">{detailProposal.presentation_date || "—"}</p></div>
                  <div><Label className="text-xs text-muted-foreground">Budget Range</Label><p className="font-medium">${detailProposal.budget_low || 0} – ${detailProposal.budget_high || 0}</p></div>
                </div>

                {/* Types */}
                {(detailProposal.proposal_types?.length > 0 || detailProposal.audit_types?.length > 0) && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Requested</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {detailProposal.proposal_types?.map((t) => <Badge key={t} variant="outline" className="text-xs">{t.replace(/_/g, " ")}</Badge>)}
                      {detailProposal.audit_types?.map((t) => <Badge key={t} variant="outline" className="text-xs bg-purple-50">{t.replace(/_/g, " ")}</Badge>)}
                    </div>
                  </div>
                )}

                {/* SEO + AEO Audit (auto-generated when requested) */}
                {detailProposal.audit_status && (
                  <ProposalAuditPanel proposal={detailProposal} onUpdate={() => openDetail(detailProposal.id)} />
                )}

                {/* Form Data */}
                {detailProposal.form_data && Object.values(detailProposal.form_data).some((v) => v) && (
                  <div className="rounded-lg border p-3 bg-zinc-50/50 space-y-1.5">
                    <h4 className="text-sm font-semibold mb-2">Proposal Details</h4>
                    {Object.entries(detailProposal.form_data).filter(([_, v]) => v).map(([k, v]) => (
                      <div key={k} className="text-sm flex gap-2">
                        <span className="text-muted-foreground capitalize min-w-[140px]">{k.replace(/_/g, " ")}:</span>
                        <span className="flex-1">{v}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Attachment + Email Section (admins/managers only) */}
                {(user?.role === "super_admin" || user?.role === "manager") && (
                  <div className="rounded-lg border-2 border-dashed border-blue-200 p-4 bg-blue-50/40 space-y-3">
                    <h4 className="text-sm font-semibold flex items-center gap-2"><Paperclip className="h-4 w-4" /> Complete & Send to Rep</h4>
                    {detailProposal.attached_filename ? (
                      <div className="flex items-center justify-between bg-white border rounded p-2 text-sm">
                        <span className="flex items-center gap-2"><Paperclip className="h-4 w-4 text-blue-600" />{detailProposal.attached_filename}</span>
                        <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingFile} data-testid="replace-file-btn">
                          {uploadingFile ? <Loader2 className="h-3 w-3 animate-spin" /> : "Replace"}
                        </Button>
                      </div>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingFile} data-testid="attach-file-btn">
                        {uploadingFile ? <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Uploading...</> : <><Paperclip className="mr-2 h-3 w-3" />Attach Proposal File</>}
                      </Button>
                    )}
                    <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileAttach} data-testid="proposal-file-input" />

                    <Textarea
                      placeholder="Optional message to include in email to the rep..."
                      value={emailMessage}
                      onChange={(e) => setEmailMessage(e.target.value)}
                      rows={2}
                      data-testid="rep-email-message"
                    />
                    <Button onClick={handleEmailToRep} disabled={emailingRep || (!detailProposal.attached_file_id && !emailMessage.trim())} className="w-full" data-testid="email-to-rep-btn">
                      {emailingRep ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</> : <><Mail className="mr-2 h-4 w-4" />Email Proposal to Rep ({detailProposal.sales_rep_email})</>}
                    </Button>
                    {detailProposal.emailed_to_rep_at && (
                      <p className="text-xs text-green-700 flex items-center gap-1"><Mail className="h-3 w-3" /> Last sent: {new Date(detailProposal.emailed_to_rep_at).toLocaleString()}</p>
                    )}
                  </div>
                )}

                {/* Rep view of attachment — now with a working download link */}
                {user?.role === "rep" && detailProposal.attached_filename && (
                  <div className="rounded-lg border p-3 bg-green-50/50 flex items-center justify-between gap-2 text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <Paperclip className="h-4 w-4 text-green-700 shrink-0" />
                      <span className="truncate">Proposal ready: <span className="font-medium">{detailProposal.attached_filename}</span></span>
                    </div>
                    {detailProposal.attached_file_id && (
                      <a
                        href={`${API_BASE}/api/files/${detailProposal.attached_file_id}/download`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md shrink-0"
                        data-testid="rep-view-proposal-btn"
                      >
                        <Eye className="h-3.5 w-3.5" /> View Proposal
                      </a>
                    )}
                  </div>
                )}

                {/* Delete (Super Admin / Manager) */}
                {(user?.role === "super_admin" || user?.role === "manager") && (
                  <div className="pt-3 border-t flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={handleDeleteProposal}
                      disabled={deletingProposal}
                      data-testid="delete-proposal-btn"
                    >
                      {deletingProposal ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting…</>
                      ) : (
                        <><Trash2 className="mr-2 h-4 w-4" /> Delete Proposal</>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * Auto-audit panel shown on the proposal detail dialog.
 * Polls for audit readiness while audit_status === "running", and surfaces
 * download (PDF / 2-Slide PPTX / Full Sales Deck) + Email-to-Prospect actions
 * once the audit is ready.
 */
function ProposalAuditPanel({ proposal, onUpdate }) {
  const [audit, setAudit] = useState(null);

  // Load the linked audit (if any) and poll while running
  useEffect(() => {
    let alive = true;
    let timer = null;

    const tick = async () => {
      if (!alive) return;
      // If we already have an audit_id, fetch the audit doc (for the Email/Download dialogs).
      if (proposal.audit_id) {
        try {
          const r = await getAudit(proposal.audit_id);
          if (alive) setAudit(r.data);
        } catch (_) { /* ignore */ }
        return;
      }
      // Otherwise, while it's still running, refresh the proposal every 5s to pick up audit_id.
      if (proposal.audit_status === "running") {
        timer = setTimeout(() => { onUpdate?.(); }, 5000);
      }
    };
    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [proposal.audit_id, proposal.audit_status, onUpdate]);

  const status = proposal.audit_status;
  if (!status) return null;

  if (status === "running" && !proposal.audit_id) {
    return (
      <div className="rounded-lg border border-purple-200 bg-purple-50/60 p-3 flex items-start gap-3" data-testid="proposal-audit-running">
        <Loader2 className="h-5 w-5 animate-spin text-purple-600 mt-0.5 shrink-0" />
        <div>
          <h4 className="text-sm font-semibold text-purple-900 flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> SEO + AEO audit in progress
          </h4>
          <p className="text-xs text-muted-foreground mt-0.5">
            Claude is auditing <b>{proposal.website_url}</b>. Usually 20–30 seconds. We&apos;ll show the report card and downloads here as soon as it&apos;s ready.
          </p>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50/60 p-3 flex items-start gap-3" data-testid="proposal-audit-failed">
        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
        <div>
          <h4 className="text-sm font-semibold text-red-900">Audit failed</h4>
          <p className="text-xs text-red-700 mt-0.5">
            {proposal.audit_error || "We couldn't reach the website or generate the audit. Open the audit builder and try again with a corrected URL."}
          </p>
        </div>
      </div>
    );
  }

  if (status === "ready" && audit) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-2" data-testid="proposal-audit-ready">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h4 className="text-sm font-semibold flex items-center gap-2 text-emerald-900">
              <Sparkles className="h-4 w-4" /> SEO + AEO audit ready
            </h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              <b>SEO Grade {audit.seo_grade}</b> ({audit.seo_score}/100) · <b>AEO Grade {audit.aeo_grade}</b> ({audit.aeo_score}/100)
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button asChild variant="outline" size="sm" data-testid="proposal-audit-pdf">
            <a href={`${API_BASE}/api/audits/${audit.id}/download?format=pdf`} download>
              <FileText className="mr-1 h-4 w-4" /> PDF
            </a>
          </Button>
          <Button asChild variant="outline" size="sm" data-testid="proposal-audit-pptx">
            <a href={`${API_BASE}/api/audits/${audit.id}/download?format=pptx`} download>
              <Download className="mr-1 h-4 w-4" /> 2-Slide PPTX
            </a>
          </Button>
          <Button asChild size="sm" className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700" data-testid="proposal-audit-branded">
            <a href={`${API_BASE}/api/audits/${audit.id}/download?format=pptx_branded`} download>
              <Presentation className="mr-1 h-4 w-4" /> Full Sales Deck
            </a>
          </Button>
          <EmailAuditDialog audit={audit} />
        </div>
      </div>
    );
  }

  return null;
}
